import type { ReactNode } from 'react';
import { Bot, X } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { MentionDetail, type MentionDetailFact } from './MentionRow';
import { cn } from '../../lib/utils';

export interface AgentRunBadgeProps {
  /** The run's display title. */
  title: string;
  /** Referenced run id, used only for a stable test id. */
  agentId?: string;
  /** Optional run details shown in the hover popover (status, started, ...). */
  facts?: MentionDetailFact[];
  /** Fallback popover body when no `facts` are supplied. */
  description?: ReactNode;
  /** When provided, renders a remove affordance (composer chips). */
  onRemove?: () => void;
  className?: string;
}

/**
 * Compact, presentational badge for a referenced agent run. Used as a removable
 * chip in the launch composer and as a read-only inline badge in the chat,
 * replacing the raw `@[...](agent-run:...)` marker and its expanded transcript
 * block. Hovering opens a popover with the run's details. Pure — callers pass
 * any live details as `facts` so this stays usable across the plugin boundary.
 */
export function AgentRunBadge({
  title,
  agentId,
  facts,
  description,
  onRemove,
  className,
}: AgentRunBadgeProps) {
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          data-testid={agentId ? `agent-run-badge-${agentId}` : 'agent-run-badge'}
          className={cn(
            'inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-muted/50 py-0.5 pr-1 pl-1.5 align-baseline text-xs text-foreground',
            className,
          )}
        >
          <Bot className="size-3 shrink-0 text-muted-foreground" />
          <span className="truncate">{title}</span>
          {onRemove ? (
            <button
              type="button"
              aria-label={`Remove ${title}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          ) : null}
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" sideOffset={6} className="w-64 p-3">
        <MentionDetail
          name={title}
          kind="Agent run"
          facts={facts && facts.length > 0 ? facts : undefined}
          description={!facts?.length ? (description ?? 'Referenced agent run') : undefined}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
